import { useCallback } from 'react';
import { useParams } from 'next/navigation';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentMethod } from '@stripe/stripe-js';
import { AlertCode, PSP } from 'shared/types/checkout';
import {
  getAccountId,
  setAlert,
  setPaymentCTAState,
  useAppDispatch,
  useAppSelector,
} from 'store';
import {
  isCurrencyAcceptedForPayment,
  resolveAcceptedCurrencies,
} from 'utils/currency';
import { useCheckout } from '../useCheckout';
import { readStripePublishableKey } from '../useCheckout/resumeTokenStorage';
import { ValidationError } from './errors';
import { validatePaymentInfoForStripe } from './helpers';
import { PlaceStripeOrderOptions } from './types';

export const useStripeOptions = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);
  const params = useParams();
  const rawLocale = params?.locale;
  const locale =
    (typeof rawLocale === 'string'
      ? rawLocale
      : Array.isArray(rawLocale)
        ? rawLocale[0]
        : undefined) ?? 'en';

  const elements = useElements();
  const stripe = useStripe();

  const { placeOrder } = useCheckout();

  const placeStripeOrder = useCallback(
    async ({
      address: { state, stateCode, ...address },
      data,
      shouldRetrySdnHit = false,
    }: PlaceStripeOrderOptions) => {
      dispatch(setPaymentCTAState({ psp: PSP.STRIPE, value: true }));

      let paymentMethod: PaymentMethod;
      try {
        paymentMethod = await validatePaymentInfoForStripe({
          stripe,
          elements,
          billingDetails: {
            state: stateCode === 'not-required' ? state : stateCode,
            ...address,
          },
        });
      } catch (error) {
        dispatch(setPaymentCTAState({ psp: PSP.STRIPE, value: false }));

        const isSilent = error instanceof ValidationError && error.silent;
        const errorMessage =
          error instanceof ValidationError
            ? error.message
            : `Validating payment info failed with error:${
                JSON.stringify(error) ?? 'unknown'
              }`;

        if (!isSilent) {
          console.error(
            `Order placement error via ${PSP.STRIPE} for customer:${customerId}. ${errorMessage}`,
          );
          dispatch(setAlert({ code: AlertCode.PAYMENT_PROCESSING_FAILED }));
        }
        return { isError: true, error };
      }

      // Settlement gate: only send the selected currency as the pay-in currency
      // when it is Monarch-accepted. Non-accepted currencies stay display-only
      // and settle in USD (backend default), so a currency can be switched off
      // via Monarch without a code deploy. Elements running on the cart-currency
      // fallback must settle there too, or the PaymentIntent would drift from
      // the amount the shopper was shown.
      const acceptedCurrencies = resolveAcceptedCurrencies(
        data.acceptedCurrencies,
      );
      const payInCurrency =
        !data.isPayInFallback &&
        isCurrencyAcceptedForPayment(
          data.selectedCurrency?.code,
          acceptedCurrencies,
        )
          ? data.selectedCurrency?.code
          : undefined;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      const result = await placeOrder({
        ...data,
        paymentMethodType:
          (paymentMethod.card?.wallet?.type as string) ?? paymentMethod.type,
        // Gate on raw PaymentMethod.type so wallets stay on the card/server path.
        stripePaymentMethodType: paymentMethod.type,
        stripe,
        stripePublishableKey: readStripePublishableKey() ?? undefined,
        locale,
        psp: PSP.STRIPE,
        pspData: paymentMethod.id,
        shouldRetrySdnHit: shouldRetrySdnHit && !address?.city,
        selectedCurrency: payInCurrency,
      });

      if (result?.isError && shouldRetrySdnHit) {
        dispatch(setPaymentCTAState({ psp: PSP.STRIPE, value: false }));

        return result;
      }

      return { isError: false };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId, stripe, elements, locale],
  );

  return { placeStripeOrder };
};
